import { IconSize } from '@thyme/nashville/src/types/icons'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { formatNameWithPreferredName } from '@/libs/format/format'
import { useWhoAmI } from '@/store/profile/whoAmI'
import {
  getPeopleDataForPointerSubtasks,
  getPointerSubtasksForStaff,
} from '@/tokens/textingNotifications/TextingNotificationsButton/queries'
import {
  usePointerSubtaskApi,
  usePointerSubtaskPersonApi,
} from '@/tokens/textingNotifications/TextingNotificationsButton/store'
import { FETCH_INTERVAL_MS } from './types'
import { renderTextReceivedStr } from './utils'

/**
 *
 */
export function setup() {
  const { selfEntity } = useWhoAmI()
  const { data: pointerSubtasks, queryMetadata } = storeToRefs(
    usePointerSubtaskApi()
  )
  const { data: memberPersonData } = storeToRefs(usePointerSubtaskPersonApi())

  const showPointerSubtaskModal = ref(false)
  const subtaskMemberEntityIds = computed(() =>
    pointerSubtasks.value?.map((s) => s.memberId)
  )
  const currentStaffId = computed(() => selfEntity.value?.entityId)
  const totalPointerSubtasks = computed(() => queryMetadata.value?.total ?? 0)

  const iconColor = computed(() =>
    totalPointerSubtasks.value > 0
      ? 'text-nash-purple400'
      : 'text-nash-neutral700'
  )
  const pointerSubtasksDisplayData = computed(() => {
    if (pointerSubtasks.value?.length) {
      return pointerSubtasks.value.map((s) => {
        const memberPerson = s.memberId
          ? memberPersonData.value?.[s.memberId]
          : null
        return {
          ...s,
          textReceivedStr: renderTextReceivedStr(s.createdAt),
          memberName: memberPerson
            ? formatNameWithPreferredName(
                memberPerson.firstName,
                memberPerson.lastName,
                memberPerson.preferredName
              )
            : 'Unknown Member',
        }
      })
    }

    return []
  })

  watch(
    () => subtaskMemberEntityIds.value,
    async (newIds) => {
      if (newIds?.length) {
        //  filter doesn't narrow the type automatically and will need to use a type predicate
        // to let TypeScript know that nulls are being filtered out (e.g. id is string)
        const validIds = newIds.filter((id): id is string => id !== null)
        if (validIds.length) {
          await getPeopleDataForPointerSubtasks(validIds)
        }
      }
    },
    { immediate: true }
  )

  let pollingInterval: ReturnType<typeof setInterval> | null = null
  /**
   * Fetch pointer subtasks for the current user
   */
  async function fetchUserPointerSubtaskstoOpenTexts() {
    if (!currentStaffId.value) return

    try {
      await getPointerSubtasksForStaff(currentStaffId.value)
    } catch (err) {
      console.error('Error fetching subtasks:', err)
    }
  }

  /**
   * Start polling for pointer subtasks every 5 minutes
   */
  function startPollingForPointerSubtasks() {
    // Clear any existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval)
    }

    // Fetch immediately
    void fetchUserPointerSubtaskstoOpenTexts()

    // Set up interval for subsequent fetches
    pollingInterval = setInterval(() => {
      void fetchUserPointerSubtaskstoOpenTexts()
    }, FETCH_INTERVAL_MS)
  }

  /**
   * Stop polling
   */
  function stopPollingForPointerSubtasks() {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
  }

  // Start polling on mount if staff ID is available
  onMounted(() => {
    if (currentStaffId.value) {
      startPollingForPointerSubtasks()
    }
  })

  // Clean up on unmount
  onBeforeUnmount(() => {
    stopPollingForPointerSubtasks()
  })

  return {
    pointerSubtasks: pointerSubtasksDisplayData,
    iconColor,
    IconSize,
    showPointerSubtaskModal,
    totalPointerSubtasks,
  }
}
